'use client'

import { Box } from '@web/components/atoms/Layout'
import { useMergeFields } from '@web/components/page-components/Text/MergeTags/MergeTag'
import {
  CAMPAIGN_MERGE_TAGS,
  CHARITY_MERGE_TAGS,
  DONOR_MERGE_TAGS,
} from '@web/components/page-components/Text/MergeTags/utils'
import { Nullable } from '@web/components/page-components/utils/types'
import { Sections, useBrandingsStore } from '@web/components/utils/branding/useBrandingsStore'
import {
  type Page as PageLayoutType,
  DonationPageLayoutFragment,
  PageMetadataQuery,
  PaymentSettingsQuery,
  ProfileDataQuery,
  PurlQuery,
  SearchBySlugQuery,
  type PageComponent as PageComponentType,
  GetThankYouPageIdFromPageIdQuery,
} from '@web/graphql-queries/graphql'
import MatchedGivingHandler from 'apps/fe-donation/components/MatchedGivingHandler'
import TestModeBanner from 'apps/fe-donation/components/TestModeBanner'
import environment from 'apps/fe-donation/libs/environment'
import { usePageStore } from 'apps/fe-donation/libs/hooks/usePageStore'
import { QUICK_PAY_PAYMENT_METHOD_TYPES, usePaymentStore } from 'apps/fe-donation/libs/hooks/usePaymentStore'
import dynamic from 'next/dynamic'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useMemo } from 'react'
import { useAutoResize } from '../_hooks/useAutoResize'
import { useEmbedMessenger } from '../_hooks/useEmbedMessenger'
import PageLayoutVariables from 'apps/fe-donation/components/PageLayoutVariables'
import { PageComponent } from 'apps/fe-donation/components/PageComponent'
import { getThankYouPageIdFromPageIdDocument } from 'apps/fe-donation/libs/queries'
import useGraphQLClient from 'apps/fe-donation/libs/hooks/useGraphQLClient'
import { ComponentType, PaymentMethodType } from '@web/graphql-types'
import { DonationForm } from '@web/graphql-components'
import FieldCollectionPopup from 'apps/fe-donation/components/FieldCollectionPopup'
import { DEFAULT_DONATION_FORM } from '@web/components/page-components/default'

const PayPalScriptProvider = dynamic(() => import('@paypal/react-paypal-js').then((mod) => mod.PayPalScriptProvider), {
  ssr: false,
})

const PayPalScriptProviderWrapper = ({
  paymentSettings,
  children,
}: {
  paymentSettings: Nullable<PaymentSettingsQuery['paymentSettings']>
  children: React.ReactNode
}) => {
  const paypalSettings = paymentSettings?.donationPagePaymentProviderSettings.payPalMerchantSetting
  if (!paypalSettings || !paypalSettings.merchantEmail || !paypalSettings.merchantId) {
    return <>{children}</>
  }

  return (
    <PayPalScriptProvider
      options={{
        clientId: paypalSettings.clientId ?? environment.payPalClientId,
        merchantId: paypalSettings.merchantId,
        currency: 'AUD',
        disableFunding: 'credit',
        components: ['buttons'],
      }}
      deferLoading={true}
    >
      {children}
    </PayPalScriptProvider>
  )
}

type EmbedPageClientProps = {
  charityId: string
  campaignId: string
  pageId: Nullable<string>
  pageBucketId: Nullable<string>
  layout: DonationPageLayoutFragment
  paymentSettings: Nullable<PaymentSettingsQuery['paymentSettings']>
  isLiveMode: boolean
  purl: PurlQuery | null | undefined
  profileId: Nullable<string>
  profileData: Nullable<ProfileDataQuery['profileData']>
  campaignSettings: SearchBySlugQuery['searchBySlug']['campaignSettings'] | null | undefined
  metadata: PageMetadataQuery['pageMetadata']
  originalDonationForm?: DonationForm | null // used to get the donation form data when using the thank you page
}

const getBrandingValuesFromString = (value: Nullable<string>) => {
  try {
    if (!value) {
      return null
    }

    const branding = JSON.parse(value)

    return {
      name: branding.name ?? '',
      logoData: branding[Sections.Logo] ?? {},
      fontData: branding[Sections.Fonts],
      colorData: branding[Sections.Colors],
      buttonData: branding[Sections.Buttons],
    }
  } catch (e) {
    return null
  }
}

const [CharityName, CharityABN, CharityPhone, CharityEmail] = CHARITY_MERGE_TAGS
const [CampaignName] = CAMPAIGN_MERGE_TAGS
const [FIRST_NAME_TAG, LAST_NAME_TAG] = DONOR_MERGE_TAGS

const EmbedPageClient = ({
  layout,
  metadata,
  purl,
  charityId,
  campaignId,
  pageId,
  pageBucketId,
  isLiveMode,
  profileId,
  profileData,
  campaignSettings,
  paymentSettings,
  originalDonationForm,
}: EmbedPageClientProps) => {
  const { broadcastEvent, redirect } = useEmbedMessenger()
  useAutoResize(true)

  const path = usePathname()
  const isThanks = path?.endsWith('/thanks') ?? false

  const searchParams = useSearchParams()
  const { footer, header, ...layoutToUse } = layout as PageLayoutType

  // Initialise page branding store
  const pageBranding = useMemo(() => getBrandingValuesFromString(layout.branding), [])
  const isThankYouPage = usePageStore((state) => state.isThankYouPage)

  const graphQLClient = useGraphQLClient()

  useEffect(() => {
    useBrandingsStore.getState().actions.loadBranding(pageBranding)
  }, [pageBranding])

  // initial page stores
  useEffect(() => {
    const charityDetails: [string, string][] = metadata?.charity
      ? [
          [CharityName.MergeFieldSettingId, metadata.charity.CharityName ?? ''],
          [CharityABN.MergeFieldSettingId, metadata.charity.ABN ?? ''],
          [CharityPhone.MergeFieldSettingId, metadata.charity.Phone ?? ''],
          [CharityEmail.MergeFieldSettingId, metadata.charity.Email ?? ''],
        ]
      : []
    const campaignDetails: [string, string][] = metadata?.campaign
      ? [[CampaignName.MergeFieldSettingId, metadata.campaign.CampaignName ?? '']]
      : []

    usePageStore.setState((state) => ({
      ...state,
      charityId,
      campaignId,
      pageId,
      pageBucketId,
      isLiveMode,
      profileId,
      isEmbed: true,
      currentUrl: state.currentUrl ?? window?.location.href,
      elementSettings: {
        progressBar: {
          pageIds: layout?.elementSettings?.progressBar?.pageIds ?? [pageId],
        },
      },
      profileData,
      campaignDomain: campaignSettings?.campaignDomain,
      amount: searchParams.get('amount') ? Number(searchParams.get('amount')) : undefined,
    }))
    usePaymentStore.setState((state) => ({ ...state, paymentSettings, hasLoaded: true }))
    useMergeFields.setState((state) => ({
      isEditing: false,
      mergeFields: new Map<string, string>([
        ...(state.mergeFields ? Array.from(state.mergeFields) : []),
        ['Giving.Amount', searchParams.get('amount') || state.mergeFields?.get('Giving.Amount') || ''],
        ...(purl ? Object.entries(purl) : []),
        ...(searchParams?.entries() ?? []),
        ...charityDetails,
        ...campaignDetails,
      ]),
    }))

    if (isThanks) {
      const paymentTransactionId = searchParams.get('ptid') ?? undefined
      const paymentSubscriptionId = searchParams.get('psid') ?? undefined

      if (!paymentTransactionId && !paymentSubscriptionId) {
        return
      }

      usePaymentStore.setState((state) => ({
        ...state,
        paymentTransactionId,
        paymentSubscriptionId,
        paymentMethodType: PaymentMethodType.GooglePay, // just set this to be a quick pay one
        showDataCollectionPopup: true,
      }))
    }
  }, [])

  useEffect(() => {
    document.documentElement.style.backgroundColor = 'transparent'

    broadcastEvent('form.loaded')
  }, [])

  useEffect(() => {
    if (!isThankYouPage) {
      return
    }

    // we don't show the thank you page on the embed form but we redirect it instead
    // (if the setting allows for it)
    // TODO: Add setting (lol)
    const performThankYouRedirect = async () => {
      const form = layout?.components?.find((c) => c.type === ComponentType.DonationForm) as DonationForm

      // don't redirect at all and just show form message
      if (form && form.styles?.options?.redirectTo === 'form-message') {
        return
      }

      const { thankYouPageIdFromId } = await graphQLClient<GetThankYouPageIdFromPageIdQuery>({
        document: getThankYouPageIdFromPageIdDocument,
        options: { campaignId, pageId },
      })

      if (!thankYouPageIdFromId) {
        // no redirect here, just keep the message on the embed form
        return
      }

      const pageState = usePageStore.getState()
      const mergeFieldsState = useMergeFields.getState()
      const paymentState = usePaymentStore.getState()
      const searchParams = new URLSearchParams({
        amount: pageState.amount?.toString() ?? '',
        purl_firstname: mergeFieldsState.mergeFields.get(FIRST_NAME_TAG.MergeFieldSettingId) ?? '',
        purl_lastname: mergeFieldsState.mergeFields.get(LAST_NAME_TAG.MergeFieldSettingId) ?? '',
        ...(isLiveMode ? {} : { mode: 'test' }),
      })

      if (paymentState.paymentMethodType && QUICK_PAY_PAYMENT_METHOD_TYPES.includes(paymentState.paymentMethodType)) {
        if (paymentState.paymentSubscriptionId) {
          searchParams.set('psid', paymentState.paymentSubscriptionId)
        }

        if (paymentState.paymentTransactionId) {
          searchParams.set('ptid', paymentState.paymentTransactionId)
        }
      }

      if (form && form.styles?.options?.redirectTo === 'external' && form.styles.options.redirectUrl) {
        // really this should be a setting outside the form but alas
        const url = new URL(form.styles.options.redirectUrl)
        url.search = searchParams.toString()
        redirect(url.toString())
        return
      }

      const url = new URL(`${window.location.origin}${window.location.pathname}/thanks`)
      url.search = searchParams.toString()

      redirect(url.toString())
    }

    performThankYouRedirect()
  }, [isThankYouPage])

  const components = new Map(layout?.components.map((c) => [c.id, c]) as [string, PageComponentType][])
  const shouldShowPopup = usePaymentStore((state) => {
    return (state.paymentTransactionId || state.paymentSubscriptionId) && state.showDataCollectionPopup
  })

  return (
    <>
      {!isLiveMode && <TestModeBanner />}
      <PayPalScriptProviderWrapper paymentSettings={paymentSettings}>
        <Box w="100%" paddingTop={isLiveMode ? '0' : '36px'}>
          <PageLayoutVariables
            key={`page-${layout.id}`}
            branding={{
              ...pageBranding,
              name: '',
              logoData: pageBranding?.logoData ?? {},
              fontData: pageBranding?.fontData ?? {},
              buttonData: pageBranding?.buttonData ?? {},
              colorData: {
                ...pageBranding?.colorData,
                Background: 'transparent',
              },
            }}
            layout={layoutToUse}
          >
            {layout?.desktop.map((c) => (
              <PageComponent key={c.id} {...c} allComponents={components} />
            ))}
          </PageLayoutVariables>
        </Box>
      </PayPalScriptProviderWrapper>
      {isThanks && shouldShowPopup && (
        <FieldCollectionPopup
          donationForm={originalDonationForm || DEFAULT_DONATION_FORM}
          branding={pageBranding}
          onSubmit={() => {
            // remove the ptid and psid from the url so that if the user refreshes they don't see the popup again
            const url = new URL(window.location.href)
            url.searchParams.delete('ptid')
            url.searchParams.delete('psid')
            window.history.replaceState(null, '', url.toString())
          }}
        />
      )}
      <MatchedGivingHandler matchedGivings={layoutToUse?.elementSettings?.matchedGivings} />
    </>
  )
}

export default EmbedPageClient
